import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  useBlockOperator,
  useDeleteOperator,
  useOperators,
  useUnblockOperator,
} from "@/api/operators";
import { CreateOperatorButton } from "./create-operator-button";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSendPasswordResetEmail } from "@/api/use-user-api";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statusColors = {
  ON_DUTY: "bg-green-500",
  OFF_DUTY: "bg-gray-500",
  BLOCKED: "bg-red-500",
};

export function OperatorsTable() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [operatorToDelete, setOperatorToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  const { data } = useOperators({
    page,
    limit,
    paginationType: "offset",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const sendPasswordResetEmail = useSendPasswordResetEmail({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Password reset email sent successfully",
      });
    },
  });

  const unblockOperator = useUnblockOperator({
    onSuccess: () => {
      toast({
        title: `Operator has been unblocked.`,
      });
    },
  });

  const blockOperator = useBlockOperator({
    onSuccess: () => {
      toast({
        title: `Operator has been blocked.`,
      });
    },
  });

  const deleteOperator = useDeleteOperator({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Operator has been deleted",
      });
      setOperatorToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete operator",
        variant: "destructive",
      });
    },
  });

  const handleDeleteOperator = (operatorId: string) => {
    setOperatorToDelete(operatorId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (operatorToDelete) {
      deleteOperator.mutate({ operatorId: operatorToDelete });
    }
    setIsDeleteDialogOpen(false);
  };

  const operators = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-4">
          <CreateOperatorButton />
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={limit.toString()}
            onValueChange={(value) => setLimit(Number(value))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Operator</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Depot</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.map((operator) => (
              <TableRow key={operator.id}>
                <TableCell className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={operator.user.picture} />
                    <AvatarFallback>
                      {operator.user.firstName[0]}
                      {operator.user.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {operator.user.firstName} {operator.user.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {operator.user.email}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${statusColors[operator.status]} text-white`}
                    variant="secondary"
                  >
                    {operator.status.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <p>{operator.user.phone}</p>
                  <p className="text-sm text-gray-500">{operator.user.email}</p>
                </TableCell>
                <TableCell>{operator.depot.name}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          sendPasswordResetEmail.mutate(operator.userId);
                        }}
                        disabled={sendPasswordResetEmail.isPending}
                      >
                        {sendPasswordResetEmail.isPending
                          ? "Sending..."
                          : "Reset Password"}
                      </DropdownMenuItem>
                      {operator.status === "BLOCKED" ? (
                        <DropdownMenuItem
                          onClick={() => {
                            unblockOperator.mutate({ operatorId: operator.id });
                          }}
                          disabled={unblockOperator.isPending}
                        >
                          {unblockOperator.isPending
                            ? "Unblocking..."
                            : "Unblock Operator"}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => {
                            blockOperator.mutate({ operatorId: operator.id });
                          }}
                          disabled={blockOperator.isPending}
                        >
                          {blockOperator.isPending
                            ? "Blocking..."
                            : "Block Operator"}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => handleDeleteOperator(operator.id)}
                        disabled={deleteOperator.isPending}
                        className="text-red-600 focus:text-red-600"
                      >
                        {deleteOperator.isPending
                          ? "Deleting..."
                          : "Delete Operator"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-500">
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, pagination.totalCount)} of{" "}
            {pagination.totalCount} results
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will permanently delete the operator and all
              associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
